<template>
  <div>
    <add-or-update-rmc-form
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />
    <h1 class="text-center my-2">RMCs</h1>

    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :show-search="true"
      :show-filter-by-default="true"
      :show-export-button="can('rmc:CanExport')"
      @export="onExport"
    >
      <template #filters>
        <b-row>
          <b-col cols="12" md="3">
            <v-select
              v-model="blockFilter"
              placeholder="Select Mission Bloc"
              :loading="blocksLoading"
              :clearable="true"
              :options="blockOptions"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>
        </b-row>
      </template>

      <template v-if="can('rmc:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add RMC</span>
        </b-button>
      </template>

      <template #cell(block)="data">
        {{ data.item.block ? data.item.block.title : '-' }}
      </template>

      <template #cell(countries)="data">
        <b-badge pill variant="light-primary" class="text-capitalize">
          {{ (data.item.countries || []).length }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item v-if="can('rmc:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('rmc:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import FileDownload from "js-file-download";
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import AddOrUpdateRmcForm from "@/@core/components/shared/geo/AddOrUpdateRmcForm.vue"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import { BRow, BCol, BButton, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BBadge,
    BButton,
    vSelect,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateRmcForm,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      blockFilter: null,
      blockOptions: [],
      blocksLoading: false,

      tableColumns: [
        { key: 'title', label: "RMC", sortable: false },
        { key: 'block', label: "Mission Bloc", sortable: false },
        { key: 'countries', label: "Countries", sortable: false },
        { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
    blockFilter: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);
    this.fetchBlocks();

    const {
      search,
      page = 1,
      limit = this.perPage,
      block_id,
    } = this.$route.query;

    if (block_id) {
      this.blockFilter = block_id;
    }

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
  },
  methods: {
    async fetchBlocks() {
      try {
        this.blocksLoading = true;

        const request = await this.useJwt().sharedService.fetchMissionBlocks({ limit: 500 });
        const { data } = request.data;

        this.blockOptions = (data || []).map((item) => ({
          label: item.title,
          value: item._id
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.blocksLoading = false;
      }
    },
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        }

        if (this.blockFilter) {
          query.block_id = this.blockFilter;
        }

        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().sharedService.fetchRmc(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.deleteRmc(id)

        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update){
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData(){
      if (this.updateData){
        this.updateData = null
      }
    },
    async onExport() {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current search and filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.search,
        }

        if (this.blockFilter) {
          query.block_id = this.blockFilter;
        }

        const response = await this.useJwt().sharedService.exportRmc(query);
        FileDownload(response.data, "rmcs-export.xlsx");
      } catch (error) {
        const error_message = get(error, "response.statusText") || get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>
